// Player data from API
export interface PlayerData {
  id: string;
  discordId: string;
  username: string;
  level: number;
  experience: number;
  gold: number;
  soulthread: number;
  vividSpools: number;
  prismaticSpools: number;
  bindings: number;
  shears: number;
  lustrousYarn: number;
  gildedYarn: number;
  faintMemory: number;
  stirringMemory: number;
  brightMemory: number;
  radiantMemory: number;
  campaignStep: number;
  discoveredStrands: string;
  discoveredEnemies: string;
  discoveredUniques: string;
  /** JSON `{ [dyeId]: count }` keyed by `dye_<color>_<tier>`. Parsed via JSON.parse. */
  dyes?: string;
}

// --- Server Combat Types ---

export interface ServerComputedAbility {
  abilityId: string;
  skillId: string;
  name: string;
  type: string;
  target: string;
  element: string;
  minDamage: number;
  maxDamage: number;
  damageEffectiveness: number;
  /** Heal-pipeline counterpart to damageEffectiveness. Scales caster.flatHealAmount into
   *  the per-cast heal (instant) or per-tick HoT contribution (Option B convention). */
  healEffectiveness?: number;
  increasedPercent: number;
  critChance: number;
  critMultiBonus: number;
  momentumCost: number;
  focusCost: number;
  masteryId?: string;
  /** Per-rank scaled status effect potency (e.g. Marked %, Empower %, Brace flat). */
  effectPotency?: number;
  /** Per-rank DoT damage (burn/bleed/poison tick). */
  dotDamage?: number;
  /** Per-rank regen-per-turn (Verdure / nature_regen). */
  regenPerTurn?: number;
  /** Per-rank charge count for charge-grant buffs (Venomcoat: 2/3/4 by rank). */
  chargesGranted?: number;
  /** Per-rank Break Power range. Rolled per cast, independent of damage. */
  breakPowerMin?: number;
  breakPowerMax?: number;
  /** Per-ability flat Break Power (rare). Added inside the increased multiplier. */
  flatBreakPower?: number;
  /** Hard cooldown in turns. The UI reads remaining turns from ServerCombatantState.abilityCooldowns. */
  cooldown?: number;
  /** Damage+barrier hybrid: status template id whose barrier is granted on-cast from barrierMin/Max. */
  appliesSelfBarrierEffect?: string;
  /** Per-rank barrier range. Read when appliesSelfBarrierEffect is set. */
  barrierMin?: number;
  barrierMax?: number;
}

export interface ServerBarrierInstance {
  sourceId: string;
  sourceName: string;
  amount: number;
  turnsRemaining: number;
  breakBehavior?: string | null;
  activeBonuses?: {
    increasedDamage?: number;
    damageReduction?: number;
    elementalRes?: number;
  };
}

export interface ServerCombatantState {
  id: string;
  name: string;
  element: string;
  defensiveTypes?: string[];
  hp: number;
  maxHp: number;
  atk?: number;
  spd: number;
  abilityIds: string[];
  skills?: ServerComputedAbility[];
  flatAddedDamage?: Record<string, { min: number; max: number }>;
  equipCritChance?: number;
  equipIncreasedCritChance?: number;
  equipCritMulti?: number;
  equipIncreasedDamage?: number;
  /** Heal-pipeline gear stats — surface for tooltip heal estimates. */
  flatHealAmount?: number;
  healingDone?: number;
  healingReceived?: number;
  healMore?: number;
  /** Strand-level Break stats (gear + mastery). Used to estimate ability Break Power in tooltips. */
  increasedBreakPower?: number;
  moreBreakPower?: number;
  flatBreakPower?: number;
  focus: number;
  maxFocus: number;
  focusRegen: number;
  /** Active barrier instances. UI sums these for display. */
  barriers: ServerBarrierInstance[];
  momentum: number;
  isAlive: boolean;
  statusEffects: ServerStatusEffect[];
  /** Tempo stacks on this target (built by attackers, consumed by spenders, resets on turn start) */
  tempo?: number;
  /** Per-enemy Break system — accumulated fill, threshold, sticky-revealed flag. */
  breakFill?: number;
  breakThreshold?: number;
  breakBarRevealed?: boolean;
  spriteKey: string;
  side: 'player' | 'enemy';
  slotIndex: number;
  unitId?: string;
  unitClass?: string;
  masteryRanks?: Record<string, number>;
  /** True for combatants spawned mid-fight via summon abilities (enemy adds, player minions). */
  isSummoned?: boolean;
  /** For player-summoned minions: id of the host combatant. */
  ownerId?: string;
  casting?: {
    abilityId: string;
    targetId: string;
    turnsRemaining: number;
  } | null;
  /** Per-ability remaining cooldown turns (decrements at this combatant's turn start, deleted at 0). */
  abilityCooldowns?: Record<string, number>;
}

export interface ServerStatusEffect {
  id: string;
  name: string;
  type: string;
  category: 'buff' | 'debuff';
  multiplier?: number;
  dotDamage?: number;
  regenAmount?: number;
  sourceId?: string;
  /** Current stack count for stackable effects (Drowning, etc). Undefined for non-stackable. */
  stackCount?: number;
  /** Scaled debuff/buff strength (Acuity: +crit chance %, etc.). */
  potency?: number;
  /** Bonus crit-multi % carried by the effect (Acuity high ranks, Catalyze). */
  bonusCritMulti?: number;
  turnsRemaining: number;
}

export type CombatStatus = 'active' | 'victory' | 'defeat' | 'fled';

export interface ServerCombatState {
  id: string;
  playerId: string;
  encounterId: string;
  status: CombatStatus;
  turn: number;
  combatants: ServerCombatantState[];
  timeline: string[];
  activeCombatantId: string;
  lastAction: CombatAction | null;
  battleLog: { turn: number; message: string }[];
  rewards: { xp: number; gold: number } | null;
  unitLevelUps?: { unitId: string; unitName: string; oldLevel: number; newLevel: number; masteryPointsGained?: number }[];
  /** Party-wide Fervor stacks (0-5) */
  fervor?: number;
  /** Decay counter — ticks +1 each player turn without a gain. At 3, lose 1 stack and reset. */
  fervorDecayTimer?: number;
  /** Active zone modifiers */
  zoneModifiers?: string[];
  /** Active Mordant Saturation Field — dye color whose theme is amplified for this fight. */
  mordantColor?: string;
}

export interface CombatAction {
  type: 'attack' | 'defend' | 'skill' | 'flee' | 'skip';
  abilityId?: string;
  targetId?: string;
}

export type CombatEventType = 'damage' | 'heal' | 'buff' | 'debuff' | 'cleanse' | 'dot_tick' | 'effect_expire' | 'defeated'
  | 'turn_start' | 'flee_success' | 'flee_fail' | 'victory' | 'defeat' | 'cast_start' | 'cast_tick' | 'summon' | 'focus_regen' | 'focus_drain' | 'tempo_gain' | 'tempo_consume' | 'fervor_gain' | 'fervor_consume' | 'break_fill' | 'break_trigger' | 'enemy_transformed';

export interface CombatEvent {
  type: CombatEventType;
  sourceId?: string;
  targetId?: string;
  value?: number;
  abilityName?: string;
  abilityId?: string;
  element?: string;
  statusEffectId?: string;
  hpAfter?: number;
  animationKey?: string;
  summonedId?: string;
  summonTemplateId?: string;
  spriteKey?: string;
  summonName?: string;
  summonHp?: number;
  summonMaxHp?: number;
  summonDefensiveTypes?: string[];
  summonHybrid?: boolean;
  summonOrdinal?: number;
  shieldDamage?: number;
  shieldAfter?: number;
  /** Snapshot of barrier instances on the target after this event */
  barriersAfter?: ServerBarrierInstance[];
  /** Marks an effect_expire event as a Barrier instance shatter (vs a status timeout) */
  barrierBreak?: boolean;
  isCrit?: boolean;
  elementMultiplier?: number;
  effectiveResistance?: number;
  shieldValue?: number;
  focusAfter?: number;
  focusCost?: number;
  focusGain?: number;
  /** For tempo_gain events: stacks added on this hit */
  tempoGained?: number;
  /** For tempo_gain events: total stacks on the target after this gain */
  tempoAfter?: number;
  /** For fervor_gain/fervor_consume events: Fervor delta (+ gained, - consumed/decayed) */
  fervorGained?: number;
  /** For fervor_gain/fervor_consume events: party-wide Fervor total after this change */
  fervorAfter?: number;
  /** For break_fill events: amount of fill added on this hit. */
  breakFillGained?: number;
  /** For break_fill / break_trigger events: target's break fill after the event. */
  breakFillAfter?: number;
  /** For break_fill / break_trigger events: target's current break threshold (post-escalation if applicable). */
  breakThreshold?: number;
  /** For break_trigger events: which break cycle this is on the target (1 = first break, 2 = second, etc). */
  breakCycle?: number;
  /** For turn_start events: snapshot of the full turn-order ID list at this moment, so the UI reshuffles before the new actor moves. */
  timeline?: string[];
  /** For enemy_transformed events: key into TRANSFORM_PRESETS selecting the
   *  bespoke marquee visual to play. Undefined falls back to the generic flash. */
  transformAnimationKey?: string;
  message: string;
}

export interface CombatActionResult {
  combatState: ServerCombatState;
  events: CombatEvent[];
}

// --- Animation definition (from server config) ---

export interface AnimationDef {
  id: string;
  name: string;
  castMotion: string;
  hitEffect: string;
  tint: number;
  castTime?: number;
  textureSwap?: boolean;
  casterGlow?: boolean;
  screenShake?: number;
  /** How long the action sprite stays visible before snapping back to idle (default 450ms) */
  actionHoldMs?: number;
}

// --- Server Ability Config ---

export interface AbilityConfig {
  id: string;
  name: string;
  type: 'damage' | 'heal' | 'cleanse' | 'summon' | 'devour' | 'buff' | 'trap';
  target: 'single_enemy' | 'single_ally' | 'all_enemies' | 'all_allies' | 'self';
  element: string;
  applySelfEffect?: string;
  applyTargetEffect?: string;
  /** Damage+barrier hybrid: status template id whose barrier is granted on-cast from rank.barrierMin/Max. */
  appliesSelfBarrierEffect?: string;
  momentumCost?: number;
  focusCost?: number;
  animationKey?: string;
  tags?: string[];
  critChance?: number;
  masteryId?: string;
  baseLifesteal?: number;
  /** % of caster Max HP healed (Grit-style self-heals). Per-rank effectPotency overrides at runtime. */
  healPercentMaxHp?: number;
  bonusVsDebuffed?: number;
  damageEffectiveness?: number;
  /** Heal-pipeline counterpart to damageEffectiveness — scales flatHealAmount per cast (instant
   *  heals) or per-tick HoT contribution (HoT-shaped buffs). */
  healEffectiveness?: number;
  castTurns?: number;
  /** Hard cooldown in turns. Player-facing lock — ability is unusable for N of the caster's own turns. */
  cooldown?: number;
  chainBounce?: boolean | number;
  /** Fulmination: extra chain hops per living Soaked enemy on the field at cast. */
  chainBonusPerSoaked?: number;
  /** Tidemancy: chain hops alternate between enemy (damage) and ally (heal). */
  chainAlternates?: boolean;
  /** Tidemancy: % of damage dealt that pulses as a heal to an ally per chain hop. */
  chainHealPercent?: number;
  /** Tidemancy: heal-chain bounces +1 ally at full potency. */
  healChainBounce?: boolean;
  /** Tempo: +N chain hops per Tempo stack consumed (Tideweave). */
  tempoConsumeChain?: number;
  cleanseTarget?: string | string[];
  /** Pure focus-restore self-buffs (Clarity). Per-rank effectPotency overrides at runtime. */
  focusRestore?: number;
  /** Tempo: +X% damage per Tempo stack consumed */
  tempoConsumeBonus?: number;
  /** Tempo: momentum penalty per Tempo stack consumed */
  tempoConsumeStun?: number;
  /** Tempo: +X% crit chance per Tempo stack consumed */
  tempoConsumeCrit?: number;
  /** Tempo: +X% crit multiplier per Tempo stack consumed */
  tempoConsumeCritMulti?: number;
  /** Tempo: pierce X points of primary-element resistance per Tempo stack consumed (clamped — never amplifies neutral/weak targets) */
  tempoConsumePen?: number;
  /** Tempo: +X% lifesteal per Tempo stack consumed on this hit */
  tempoConsumeLeech?: number;
  /** Tempo: hit count = baseHitCount + (target.tempo × tempoConsumeHits). Reprise spender. */
  tempoConsumeHits?: number;
  /** Tempo: refund N momentum to caster per hit while spending Tempo. Reprise initiative-control rider. */
  tempoConsumeMomentumRefund?: number;
  /** Tempo: generates X Tempo stacks on hit */
  tempoGenerate?: number;
  /** Tempo: generates X Tempo stacks on hit only when attacking from Stealth (Vanish buff active) */
  tempoGenerateFromStealth?: number;
  /** Tempo: generates X Tempo stacks only on crit hits */
  tempoOnCrit?: number;
  /** Fervor: +X% damage per Fervor stack consumed (spender) */
  fervorConsumeBonus?: number;
  /** Fervor: applied debuff's potency scales by fervorConsumeBonus% per Fervor stack (Blight). */
  fervorScalesEffectPotency?: boolean;
  /** Fervor: applied debuff's duration extends by +1 turn per N Fervor stacks consumed (Immolate, value = divisor). */
  fervorExtendsEffectTurnsPer?: number;
  /** Fervor: lifesteal % gains this much per Fervor stack consumed (Devour). */
  fervorLifestealPerStack?: number;
  /** Fervor: generates X Fervor stacks just by casting */
  fervorOnCast?: number;
  /** Fervor: generates X Fervor stacks when this ability lands a kill */
  fervorOnKill?: number;
  /** Fervor: generates X Fervor on super-effective (vs dark / element weakness) */
  fervorOnSuperEffective?: number;
  /** Fervor: generates X Fervor when attacking from Stealth */
  fervorOnStealth?: number;
  /** Fervor: generates X Fervor per Imprint consumed (Mindbreak rider) */
  fervorOnImprint?: number;
  /** Fervor: generates X Fervor per cast that lands at least one crit (cap 1/cast) */
  fervorOnCrit?: number;
  /** Fervor: generates X Fervor when the rot debuff is consumed by a DoT tick */
  fervorOnDotConsume?: number;
  /** Break: per-ability intrinsic Break Power range (rolls each cast, parallel to damage range). */
  breakPowerMin?: number;
  breakPowerMax?: number;
  /** Break: flat additive to the fill, added inside the increased multiplier alongside the range. */
  flatBreakPower?: number;
  /** Break: +X% damage rider when the target is in the Broken state. */
  bonusVsBroken?: number;
  /** Break: when this hits a Broken enemy, every party ally heals for this % of their Maximum HP. */
  partyHealPercentMaxHpVsBroken?: number;
  /** Break: applies this status template to the target if and only if the target is Broken at hit-resolve time (Lithurgy/Megalith signature). */
  applyTargetEffectOnBroken?: string;
  /** Illusionism: apply X Imprint stacks on the target per cast */
  imprintGenerate?: number;
  /** Illusionism: consume all Imprint stacks from the target for bonus damage */
  imprintConsume?: boolean;
  /** Illusionism: +X% damage per Imprint stack consumed */
  imprintConsumeBonus?: number;
  /** Bonus damage against targets below 30% Health (Execute) */
  bonusVsLowHp?: number;
  /** Multi-hit: number of hits per use */
  hitCount?: number;
  /** Spreads this effect to other enemies */
  spreadEffect?: string;
  /** Conditional lifesteal: effect that must be on target */
  lifestealVsEffect?: string;
  /** Conditional lifesteal: percentage when condition met */
  lifestealVsPercent?: number;
  /** Explicit min damage/heal for enemy abilities */
  minDamage?: number;
  /** Explicit max damage/heal for enemy abilities */
  maxDamage?: number;
  /** Player-summon: minion template ID to spawn (only for type: 'summon') */
  summonTemplateId?: string;
  /** Player-summon: group name for caps. Skills sharing a group share their cap. */
  summonGroup?: string;
  /** Player-summon: max simultaneous minions in this group per host (default 1). */
  summonGroupMax?: number;
}

// --- Crawl Types ---

export type LightingType = 'daylight' | 'moonlight' | 'candlelight' | 'weavelight';

export interface CrawlConfig {
  id: string;
  name: string;
  encounters: { tier: string; encounterId: string; bgKey?: string }[];
  completionBonus: { xp: number; gold: number };
  dropItemLevel: number;
  unitDropPool?: string[];
  itemDropPool?: string[];
  bgKey: string;
  lightingType?: LightingType;
  featuredSkinId?: string;
  eventExpiresAt?: string;
  zoneModifiers?: string[];
  guaranteedCampaignDrops?: {
    itemBaseId?: string | string[];
    itemDisplayName?: string;
    itemRarity?: string;
  };
}

export interface CrawlRunState {
  id: string;
  date: string;
  crawlConfigId: string;
  encounterIndex: number;
  status: 'in_progress' | 'completed' | 'failed';
  encounters: string[];
  unitHpSnapshots: { unitId: string; hp: number; maxHp: number }[];
  completedAt: string | null;
  forkChosen?: string | null;
  pendingFork?: {
    paths: Record<string, { label: string; description: string; hints: string[]; elements: string[] }>;
  } | null;
}

export interface EventFeatureSet {
  unitClass: string;
  skinId: string;
}

export interface ActiveEventBundle {
  id: string;
  name: string;
  subtitle: string;
  accentColor: string;
  shopId: string;
  /** Optional custom shop bg path. Falls back to default shop bg if omitted. */
  shopBgUrl?: string;
  /** Omitted for shop-only events with no crawl. */
  crawlId?: string;
  /** "Go win it" — featured on the home page event card. Omitted for shop-only events. */
  homeSet?: EventFeatureSet;
  /** "Go buy it" — featured on the shop event hero. */
  shopSet: EventFeatureSet;
}

export interface CrawlData {
  config: CrawlConfig;
  run: CrawlRunState | null;
  /** Present on event crawls — shared theme metadata for the active event bundle */
  activeEvent?: ActiveEventBundle;
}

export interface CampaignStepState {
  stepIndex: number;
  crawlId: string;
  title: string;
  description: string;
  encounterCount: number;
  status: 'locked' | 'available' | 'completed';
  bgKey: string;
  dropItemLevel: number;
  rewardTypes: string[];
  guaranteedRewards: string[];
  /** Most iconic enemy sprite for this chapter — used by the home page
   *  campaign hero card. Computed server-side via pickIconicEnemySpriteKey. */
  previewSpriteKey?: string;
  /** Defensive types from the early portion of the chapter, with `partial`
   *  set when later encounters / fork paths bring in additional types. Drives
   *  the home-card element badge row. */
  previewElements: { visible: string[]; partial: boolean };
}

export interface CampaignData {
  campaignStep: number;
  steps: CampaignStepState[];
  inProgressRun: CrawlRunState | null;
}

// --- Affix / Item Types ---

export interface AffixTier {
  requiredLevel: number;
  minValue: number;
  maxValue: number;
  minHigh?: number;   // For flat damage dual-roll: minValue=minLow, minHigh, maxValue=maxLow, maxHigh
  maxHigh?: number;
  weight?: number;
}

export interface AffixInstance {
  affixId: string;
  roll: number;
  tier: number;  // index into tiers array (0-based)
  minRoll?: number;  // flat damage: min damage roll
  maxRoll?: number;  // flat damage: max damage roll
}

export interface AffixDef {
  id: string;
  name: string;
  type: 'implicit' | 'prefix' | 'suffix';
  stat: string;
  category: 'combat_stat' | 'mastery' | 'mastery_cost_reduction' | 'flat_damage' | 'resistance' | 'penetration' | 'tag_increased';
  element?: string;
  masteryId?: string;
  conditionTag?: string;
  modifier: 'flat' | 'percent';
  tiers: AffixTier[];
  tags: string[];
  weight: number;
  /** Storage scale for sub-integer precision. Stored `roll` is 10^decimals × applied value. */
  decimals?: number;
}

export interface MasteryRequirement {
  masteryId: string;
  rank: number;
}

export interface SkillRank {
  minDamage: number;
  maxDamage: number;
  masteryRequirements?: MasteryRequirement[];
  dotDamage?: number;
  regenPerTurn?: number;
  effectPotency?: number;
  effectDuration?: number;
  momentumCost?: number;
  focusCost?: number;
  /** Player-summon skills (e.g. raise_skeleton): minion HP at this rank. */
  summonHp?: number;
  /** Player-summon skills: turns the minion lasts before automatic expiry. Undefined = persists until killed. */
  summonDurationTurns?: number;
  /** Damage+barrier hybrid: per-rank barrier range. Read when ability has appliesSelfBarrierEffect. */
  barrierMin?: number;
  barrierMax?: number;
  /** Per-rank override for advanceAllyMomentum (Coordinate-shape: timeline push on cast). */
  advanceAllyMomentum?: number;
}

export interface SkillDef {
  id: string;
  name: string;
  abilityId: string;
  tags: string[];
  ranks: SkillRank[];
  /** Internal skills (e.g. minion companion abilities like bone_slash) — never shown in the player picker. */
  internal?: boolean;
}

export interface ItemBaseDef {
  id: string;
  name: string;
  category: 'weapon' | 'armor' | 'accessory' | 'offhand';
  family: string;
  ilvl: number;
  charLevelRequired: number;
  tags: string[];
  implicitAffixId: string;
  masteryRequirements?: MasteryRequirement[];
  /** Weapon only. '2h' weapons block 'held' offhands. */
  handedness?: '1h' | '2h';
  /**
   * Offhand only. 'held' offhands can't pair with '2h' weapons; 'quivered' offhands require a bow in the mainhand;
   * 'worn' offhands have no mainhand coupling.
   */
  grip?: 'held' | 'worn' | 'quivered';
  /** Excluded from generated drop pools — family is shipped but not yet droppable. */
  parked?: boolean;
}

/** Unique item definition — references a base via `baseId` and overrides selected fields. */
export interface UniqueItemData {
  id: string;
  /** The ITEM_BASES entry this unique derives from (provides category/family/tags/implicit/handedness/grip). */
  baseId: string;
  uniqueName: string;
  flavorText: string;
  fixedExplicits: { affixId: string; roll: number }[];
  dropIlvlRange: [number, number];
  ilvl: number;
  charLevelRequired: number;
  /** Override base's masteryRequirements. [] removes all reqs. Omit to inherit from base. */
  masteryRequirements?: MasteryRequirement[];
  /** Override base's implicit affix. Omit to inherit. */
  implicitAffixId?: string;
}

export interface ItemData {
  id: string;
  playerId: string;
  category: 'weapon' | 'armor' | 'accessory' | 'offhand';
  baseType: string;
  name: string;
  level: number;
  rarity: string;
  implicitAffix: string;     // JSON: AffixInstance
  explicitAffixes: string;   // JSON: AffixInstance[]
  equippedToUnitId: string | null;
  inventorySlot: number | null;
  /** Pre-computed by backend. E.g. "Bladecraft 3". Empty string if no requirements. */
  requirementsDisplay: string;
}

// --- Mastery Types ---

export type MasteryCategory = 'primal' | 'cosmic' | 'martial' | 'cunning';

export interface MasteryRankBonus {
  type: 'flatStat' | 'percentStat' | 'tagIncreased' | 'special';
  stat: string;
  perRank: number;
  label: string;
}

export interface MasteryDef {
  id: string;
  name: string;
  icon?: string;
  element: string;
  category: MasteryCategory;
  status: 'active' | 'nyi';
  description: string;
  damageTag?: string;
  rankBonus?: MasteryRankBonus;
}

// --- Unit Types ---

export interface SkillEstimate {
  skillId: string;
  name: string;
  element: string;
  type: string;
  minDamage: number;
  maxDamage: number;
  increasedPercent: number;
  critChance: number;
  critMulti: number;
  momentumCost: number;
  lifesteal: number;
}

export interface ComputedStats {
  hp: number;
  spd: number;
  focus: number;
  focusRegen: number;
  focusOnCrit: number;
  focusOnKill: number;
  focusOnHit: number;
  masteryRanks: Record<string, number>;
  effectiveMasteryCosts: Record<string, number>;
  equipCritChance: number;
  equipIncreasedCritChance: number;
  equipCritMulti: number;
  equipLifesteal: number;
  equipIncreasedDamage: number;
  equipDotPotency: number;
  gearResistance: Record<string, number>;
  penetration: Record<string, number>;
  resistanceProfile: Record<string, number>;
  tagIncreased: Record<string, number>;
  flatAddedDamage: Record<string, { min: number; max: number }>;
  // Sustain
  lifeOnHit: number;
  hpRegen: number;
  shieldOnTurnStart: number;
  // Barrier
  barrierOnHit: number;
  barrierOnCrit: number;
  barrierDuration: number;
  barrierActiveRes: number;
  barrierActiveDr: number;
  barrierActiveDamage: number;
  // Healing
  healingDone: number;
  healingReceived: number;
  flatHealAmount: number;
  healMore: number;
  // DoT & Status
  dotMultiplier: number;
  flatDotDamage: number;
  statusResistance: number;
  dotDamageTaken: number;
  chanceInflictExposed: number;
  // Mastery & gear bonuses
  masteryFlatDamage: number;
  masteryBonusVsDebuffed: number;
  masteryMomentumReduction: number;
  masteryFocusCostReduction: number;
  masteryBarrierPotency: number;
  hexPotency: number;
  ruinPotency: number;
  debuffDuration: number;
  buffDuration: number;
  debuffDurationFlat: number;
  buffDurationFlat: number;
  boonPotency: number;
  impactDamage: number;
  fervorPotency: number;
  tempoGeneration: number;
  staggerPotency: number;
  bonusVsFullHp: number;
  // Minions
  minionDamageIncreased: number;
  minionHealthIncreased: number;
  minionSpd: number;
  minionCritChance: number;
  minionCritMulti: number;
  // Combo / chain
  extraComboHits: number;
  chainChance: number;
  // Defense
  guard: number;
  guardPercent: number;
  healOnKill: number;
  healOnCombatEnd: number;
  // Light/medium armor reactive
  corneredSpd: number;
  momentumOnStruck: number;
  hpOnStruck: number;
  focusOnStruckArmor: number;
  thornsDamage: number;
  critAvoidance: number;
  firstStrikeBonus: number;
  speedOnKill: number;
  // Per-element DoT potency
  burnDotPotency: number;
  bleedDotPotency: number;
  poisonDotPotency: number;
  decayDotPotency: number;
  skillEstimates: SkillEstimate[];
}

export const SKILL_SLOT_COUNT = 4;
export const SKILL_SLOT_KEYS = ['skill0Id', 'skill1Id', 'skill2Id', 'skill3Id'] as const;
export type SkillSlotKey = typeof SKILL_SLOT_KEYS[number];

export interface PartyUnit {
  id: string;
  unitClass: string;
  name: string;
  race: string;
  element: string;
  defensiveTypes?: string[];
  hp: number;
  spd: number;
  equipped: boolean;
  rarity: string;
  itemLevel: number;
  level: number;
  experience: number;
  /** XP progress into current level. Pre-computed by backend. */
  xpIntoLevel: number;
  /** XP needed to reach next level from start of current level. 0 at cap. */
  xpToNextLevel: number;
  unspentMasteryPoints: number;
  masteryRanks: string;   // JSON: Record<string, number>
  masteryCosts: string;   // JSON: Record<string, number>
  implicitAffix: string | null;
  explicitAffixes: string | null;
  weaponId: string | null;
  armorId: string | null;
  accessoryId: string | null;
  offhandId: string | null;
  skill0Id: string | null;
  skill1Id: string | null;
  skill2Id: string | null;
  skill3Id: string | null;
  activeSkinId: string | null;
  computedStats: ComputedStats;
  /** All skills this unit can equip, pre-computed from mastery ranks */
  unlockedSkills?: { id: string; name: string; abilityId: string; rank: number; masteryId: string }[];
}

export interface SkinConfig {
  id: string;
  name: string;
  unitClasses: string[];
  spriteKey: string;
}

export interface UnitDropData {
  id: string;
  unitClass: string;
  name: string;
  race: string;
  element: string;
  defensiveTypes?: string[];
  rarity: string;
  itemLevel: number;
  hp: number;
  spd: number;
  masteryRanks: string;
  masteryCosts: string;
  implicitAffix: string | null;
  explicitAffixes: string | null;
  computedStats: ComputedStats;
}

export interface UnitTemplate {
  id: string;
  name: string;
  race: string;
  element: string;
  defensiveTypes?: string[];
  spriteKey: string;
  hp?: number;
  hpGrowth?: number;
  spd?: number;
  focus?: number;
  focusRegen?: number;
  masteryCosts?: Record<string, number>;
  implicitPool?: { affixId: string; weight: number }[];
  gender?: string;

  // ── Strand Discovery Reveal fields (optional, populated incrementally) ──
  /** 2-4 word player-facing classification (loremaster-owned) */
  archetype?: string;
  /** Italicized pull-quote shown in the discovery modal (loremaster-owned) */
  pullQuote?: string;
  /** 2-4 sentence in-world backstory (loremaster-owned) */
  loreParagraph?: string;
  /** "Lean into" gameplay hint phrases (warden/strandwright-owned) */
  leanInto?: string[];
  /** Strand classes that pair well (warden/strandwright-owned) */
  playsWellWith?: string[];
}

export interface EnemyFormTransition {
  /** Trigger when current HP / maxHp drops below this percent (0-100). */
  atHpPercent: number;
  /** Template ID to swap into when the threshold is crossed. */
  templateId: string;
}

export interface EnemyTemplate {
  id: string;
  name: string;
  element: string;
  tier?: string;
  defensiveTypes?: string[];
  hp: number;
  atk?: number;
  spd: number;
  abilityIds: string[];
  spriteKey: string;
  xpReward: number;
  goldReward: number;
  resistances?: Record<string, number>;
  /** Multi-form bosses swap templates at HP thresholds. Listed in HP-descending
   *  order on each form (form 1 → form 2 → form 3). The form-target templates
   *  are themselves enemy templates and may have their own formTransitions. */
  formTransitions?: EnemyFormTransition[];
}

export interface UnitData {
  roster: PartyUnit[];
  party: PartyUnit[];
  ownedSkins?: Record<string, number>;
}

// --- Unified State ---

export interface CrawlDetailEnemy {
  name: string;
  element: string;
  defensiveTypes?: string[];
  spriteKey: string;
}

export interface CrawlDetailEncounter {
  name: string;
  tier: string;
  enemies: CrawlDetailEnemy[];
}

// ─── Patterns (endgame map items) ───────────────────────────────
export interface PatternAffixData {
  id: string;
  rollValue: number;
  dangerValue: number;
  quantity: number;
  rarity: number;
  packSize: number;
}

export interface PatternStashItem {
  id: string;
  typeId: string;
  typeName: string;
  typeDescription: string;
  bgKey: string;
  seed: string;
  patternLevel: number;
  /** Actual enemy level (35 for T1, +3 per tier). What players see in the UI. */
  enemyLevel: number;
  rarity: 'normal' | 'magic' | 'rare';
  affixes: PatternAffixData[];
  rooms: [number, number];
}

export interface CrawlDetails {
  config: CrawlConfig;
  encounters: CrawlDetailEncounter[];
  rewards: {
    completionXP: number;
    completionGold: number;
    dropItemLevel: number;
    possibleUnits: { id: string; name: string }[];
    possibleItems: { id: string; name: string }[];
    currencyDrops: { name: string; icon: string; rarity: string }[];
  };
}

export interface UnifiedState {
  playerData: PlayerData;
  combatData: ServerCombatState | null;
  unitData: UnitData;
  itemData: ItemData[];
  notificationData: NotificationData;
  coachData: CoachData;
  crawlData: CrawlData | null;
  eventCrawlData: CrawlData | null;
  campaignData: CampaignData | null;
  configData: ConfigData;
  achievementData: AchievementSummary;
  isAdmin?: boolean;
}

export interface AchievementDef {
  id: string;
  name: string;
  description: string;
  category: 'story' | 'combat' | 'crafting' | 'collection' | 'meta';
  hidden?: boolean;
  steamApiName: string;
  threshold?: number;
  steamStatName?: string;
}

export interface AchievementSummary {
  unlocked: string[];
  counters: Record<string, number>;
  newly: string[];
  steamStats: Record<string, number>;
}

export interface CoachTip {
  id: string;
  title: string;
  text: string;
  target?: 'party' | 'craft' | 'inventory' | 'soulloom';
}

export interface CoachData {
  pending: string[];
  tips: Record<string, CoachTip>;
}

export interface StatusEffectTemplate {
  id: string;
  name: string;
  type: string;
  category: 'buff' | 'debuff';
  /** Gear-bucket / cleanse-bucket tag. Buffs default to 'boon' when unset. */
  subcategory?: 'boon' | 'barrier' | 'regen' | 'hex' | 'ruin';
  source?: 'player' | 'enemy' | 'both';
  description?: string;
  multiplier?: number;
  dotDamage?: number;
  regenAmount?: number;
  momentumPenalty?: number;
  resistanceModifiers?: Record<string, number>;
  /** Trap: element the trap deals damage with when it springs. */
  trapElement?: string;
  /** Trap: % damage bonus per Tempo stack consumed on spring. */
  trapPerStackBonus?: number;
  /** Trap: trigger condition evaluated at start of target's turn. */
  trapTriggerCondition?: string;
  /** Marks effects that grant a Barrier instance whose amount is read from the skill rank's damage range. */
  grantsBarrier?: boolean;
  /** Additional mechanic lines for this barrier (e.g. "Enemies that hit this Barrier are Chilled for 1 turn") */
  barrierEffects?: string[];
  /** Override element label for DoT display (e.g. poison always shows "Nature" regardless of parent ability) */
  dotElement?: string;
  /** Stackable effects: repeated applications increment stackCount up to maxStacks. */
  stackable?: boolean;
  maxStacks?: number;
  defaultTurns: number;
}

export interface ConfigData {
  abilities: Record<string, AbilityConfig>;
  animations: Record<string, AnimationDef>;
  unitTemplates: Record<string, UnitTemplate>;
  statusEffects: Record<string, StatusEffectTemplate>;
  affixes: Record<string, AffixDef>;
  itemBases: Record<string, ItemBaseDef>;
  uniqueBases?: Record<string, UniqueItemData>;
  masteries: Record<string, MasteryDef>;
  masteryTierSize?: number;  // Deprecated: flat cost formula no longer uses tier size
  masteryPointsTable: number[];
  currencies?: Record<string, CurrencyDef>;
  dyes?: Record<string, DyeDef>;
  skins?: Record<string, SkinConfig>;
  skillDefs?: Record<string, SkillDef>;
  enemies?: Record<string, EnemyTemplate>;
  /** Player-summon minion templates. Keyed by template ID (e.g. 'skeleton'). Used by summon-ability tooltips. */
  minionTemplates?: Record<string, EnemyTemplate>;
  elementChart?: Record<string, Record<string, number>>;
  achievements?: AchievementDef[];
}

// --- Soulloom Types ---

export interface SoulloomResult {
  outputUnit: PartyUnit;
  bonusAffixAdded: boolean;
}

/** Dye config — mirrors src/config/dyes.ts DYE_REGISTRY shape on the wire.
 *  Used by the workshop dye picker to look up the actual affix granted per
 *  slot and the rolled value range per tier. */
export interface DyeSlotMapping {
  affixId: string;
  paleIdx: number;
  deepIdx: number;
  saturatedIdx: number;
}

export interface DyeDef {
  color: string;
  name: string;
  theme: string;
  hexColor: string;
  saturationField: string;
  slots: {
    weapon: DyeSlotMapping;
    armor: DyeSlotMapping;
    offhand: DyeSlotMapping;
    accessory: DyeSlotMapping;
  };
}

export interface CurrencyDef {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  displayOrder: number;
  /** Screen IDs where this currency badge appears in the wallet header.
   *  Undefined = always visible (e.g. Gold). */
  visibleOn?: string[];
}

export interface CraftResult {
  entity: PartyUnit | ItemData;
}

export interface CurrencyDrop {
  currencyId: string;
  amount: number;
}

// Shop types
export interface ShopListingData {
  id: string;
  slotIndex: number;
  listingType: 'item' | 'unit' | 'currency' | 'skin';
  source: 'daily' | 'event';
  entityData: string;
  displayName: string;
  rarity: string;
  level: number;
  price: number;
  purchased: boolean;
}

// --- Social Presence Types ---

export interface DiscordParticipant {
  id: string;
  username: string;
  avatar: string | null;
  global_name?: string | null;
  nickname?: string;
}

export interface PresenceData {
  participants: DiscordParticipant[];
  self: DiscordParticipant | null;
}

export interface InspectData {
  discordId: string;
  username: string;
  level: number;
  campaignStep: number;
  party: InspectUnit[];
}

export interface InspectItemData {
  name: string;
  baseType: string;
  category: 'weapon' | 'armor' | 'accessory' | 'offhand';
  rarity: string;
  level: number;
  implicitAffix: string;
  explicitAffixes: string;
}

export interface InspectUnit {
  unitClass: string;
  name: string;
  race: string;
  element: string;
  defensiveTypes?: string[];
  level: number;
  rarity: string;
  computedStats: ComputedStats;
  weapon: InspectItemData | null;
  armor: InspectItemData | null;
  accessory: InspectItemData | null;
  skillIds: string[];
  masteryRanks: Record<string, number>;
  activeSkinId: string | null;
}

// State keys for StateManager
export interface NotificationData {
  newItems: boolean;
  newStrands: boolean;
  strandAlerts: Record<string, boolean>;
}

export type StateKey = 'playerData' | 'uiState' | 'combatData' | 'crawlData' | 'eventCrawlData' | 'campaignData' | 'configData' | 'unitData' | 'itemData' | 'shopData' | 'presenceData' | 'notificationData' | 'coachData' | 'isAdmin' | 'achievementData';

// Game state shape
export interface GameState {
  playerData: PlayerData | null;
  uiState: UIState | null;
  combatData: ServerCombatState | null;
  crawlData: CrawlData | null;
  eventCrawlData: CrawlData | null;
  campaignData: CampaignData | null;
  configData: ConfigData | null;
  unitData: UnitData | null;
  itemData: ItemData[] | null;
  shopData: ShopListingData[] | null;
  presenceData: PresenceData | null;
  notificationData: NotificationData | null;
  coachData: CoachData | null;
  isAdmin: boolean;
  achievementData: AchievementSummary | null;
}

// UI-specific state
export interface UIState {
  activePanel: string | null;
  isLoading: boolean;
}

// Command result
export interface CommandResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

// Command context for invalidation
export interface CommandContext {
  invalidateKeys?: StateKey[];
  successMessage?: string;
}

// Command interface for action pattern
export interface Command<TParams = void, TResult = void> {
  name: string;
  execute(params: TParams): Promise<CommandResult<TResult>>;
  getContext(params: TParams): CommandContext;
}

// ---------------------------------------------------------------------------
// Combat encounter types (for Phaser scene)
// ---------------------------------------------------------------------------

export interface CombatantDef {
  id: string;
  spriteKey: string;
  name: string;
  hp: number;
  maxHp: number;
}

export interface CombatSceneData {
  players: CombatantDef[];   // 1-3 strands at fight start; can grow up to 6 with summoned minions
  enemies: CombatantDef[];   // 1-6
  activeCombatantId?: string;
  bgKey?: string;
  lightingType?: LightingType;
  /**
   * When set, this fight is a Mordant. The CombatScene renders a chromatic
   * overlay (vignette + drifting motes) in the dye color. Bone uses bone-white
   * + skeletal motes. Saturation Field combat math is already applied
   * server-side; this is purely visual.
   */
  mordantColor?: 'crimson' | 'verdant' | 'azure' | 'citrine' | 'pearl'
    | 'ochre' | 'slate' | 'bronze' | 'silver' | 'gilt'
    | 'pitch' | 'violet' | 'bone';
}

// ---------------------------------------------------------------------------
// Combat HUD types
// ---------------------------------------------------------------------------

/** Screen-space position of a combatant sprite, emitted by CombatScene */
export interface CombatantPosition {
  id: string;
  x: number;
  y: number;
  displayHeight: number;
  isEnemy: boolean;
}

/** Payload for COMBATANT_POSITIONS_UPDATED event */
export interface CombatantPositionsPayload {
  positions: CombatantPosition[];
}

/** A single entry in the speed timeline */
export interface TimelineEntry {
  id: string;
  label: string;
  isEnemy: boolean;
  isActive: boolean;
  spriteKey?: string;
  /** Current momentum (used for preview computation) */
  momentum?: number;
  /** Combatant speed (used for preview tick simulation) */
  spd?: number;
  /** Which side this combatant is on (used for preview tie-breaking) */
  side?: 'player' | 'enemy';
  /** Slot index (used for preview tie-breaking) */
  slotIndex?: number;
}

/** Targeting mode for an ability */
export type TargetingMode = 'single' | 'all' | 'self' | 'ally_single' | 'ally_all';

/** Ability available to the active character */
export interface AbilityDef {
  id: string;
  name: string;
  /** EventBus event to emit when pressed */
  action: string;
  /** How the ability selects targets */
  targeting: TargetingMode;
}
